<template>
  <!-- Messages -->
  <div>
    <div ref="messagesContainer" class="conversation-container bg-white">
      <div class="scrollUp" :class="{ hidden: messagesScrolledToTop }" @click="scrollTop">
        <span>Scroll opp</span>
      </div>
      <div class="pl-4 pb-4 pr-4">
        <div
          v-for="(message, i) in prospect.messages"
          :key="message.id"
          class="chat"
          :class="getSenderClass(message.sender)"
        >
          <div v-if="lastMessageDateDifferenceMoreThanOneDay(i)" class="date-stamp">
            <a class="date-stamp-inner">{{ getMessageDate(message.date) }}</a>
          </div>

          <div ref="messages" :data-index="i">
            <p>
              <span class="from">{{ getSender(message.sender, prospect.name) }}</span>
              <span class="timestamp">
                -
                <hl-Date :date="message.date" format="LT" utc />
              </span>
            </p>
            <pre class="message d-inline-block">{{ message.message }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div class="sendMessageSection">
      <textarea placeholder="Skriv melding" v-model="newMessage" />

      <hl-Button variant="primary" @click="sendMessage">
        <i class="budicon-solid budicon-solid-sending-message" />
      </hl-Button>
    </div>
  </div>
  <!-- / Messages -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import * as moment from 'moment';
import { nanoid } from 'nanoid';
import { debounce } from 'lodash';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Textarea from '@modules/husleie-framework/src/components/forms/Textarea.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import { InterestManagementCandidateApi } from '@modules/husleieapi2';

const api = new InterestManagementCandidateApi();

export default {
  components: {
    'hl-Date': Date,
    'hl-Textarea': Textarea,
    'hl-Button': Button,
  },

  props: {
    prospectId: [Number, String],
    landlordId: [Number, String],
    iMId: String,
  },

  data() {
    return {
      id: nanoid(3),
      messagesScrolledToTop: false,
      newMessage: '',
      messagesObserver: undefined,
      trackedVisibilityMessages: new Set(),
      lastSeenDate: undefined,
      storeLastSeenDebounced: debounce(this.storeLastSeen, 1000),
      storeLastSeenActiveRequest: undefined,
      storeLastSeenBufferValue: undefined,
    };
  },

  computed: {
    ...mapGetters(['getProspectDetails', 'isUserSudo']),

    prospect() {
      return this.getProspectDetails(this.$route.params.iMId, this.prospectId);
    },
  },

  methods: {
    ...mapActions(['addNewMessage']),

    async sendMessage() {
      if (!this.newMessage) {
        return;
      }

      try {
        await this.addNewMessage({
          landlordId: this.landlordId,
          prospectId: this.prospectId,
          message: this.newMessage,
          iMId: this.iMId,
        });
        this.newMessage = '';
        this.$toast('Melding sendt.', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke sende melding.', 'error');
      }
    },

    getSenderClass(sender) {
      if (sender === 'landlord') {
        return 'chat-origin';
      }

      if (sender === 'system') {
        return 'chat-system';
      } else return 'chat-destination';
    },

    getSender(sender, name) {
      if (sender === 'landlord') {
        return 'Du';
      }

      if (sender === 'system') {
        return 'Husleie.no';
      }

      if (sender === 'tenant') {
        return name;
      }
      return '';
    },

    scrollTop() {
      this.$nextTick(() => {
        const element = this.$refs.messagesContainer;
        element.scrollTo({ top: 1, behavior: 'smooth' });
      });
    },

    handleScroll(event) {
      if (event.srcElement.scrollTop === 0) {
        this.messagesScrolledToTop = true;
      } else {
        this.messagesScrolledToTop = false;
      }
    },

    getMessageDate(d) {
      const today = moment();
      const date = moment.utc(d);

      const difference = date.diff(today, 'days');

      if (difference >= -2) {
        return date.fromNow();
      } else return moment(date).format('MMM Do YY');
    },

    lastMessageDateDifferenceMoreThanOneDay(i) {
      const lastMessageDate = moment(this.prospect.messages[i].date);
      let secondLastMessageDate = null;
      let isFirstMessage = false;

      if (i > 0) {
        secondLastMessageDate = moment(this.prospect.messages[i - 1].date);
      } else {
        isFirstMessage = true;
        secondLastMessageDate = moment(this.prospect.messages[0].date);
      }

      const isSameDay = secondLastMessageDate.isSame(lastMessageDate, 'day');

      const difference = lastMessageDate.diff(secondLastMessageDate, 'days');
      return difference >= 1 || isFirstMessage || !isSameDay;
    },

    updateLocalLastSeenDate() {
      if (this.prospect.messages.length === 0) return;
      this.lastSeenDate = this.prospect.messages[0].last_read;
    },

    isMessageSeen(message) {
      return this.lastSeenDate && message.date <= this.lastSeenDate;
    },

    trackMessagesVisibility() {
      if (this.isUserSudo) return;

      this.prospect.messages.forEach((message, i) => {
        if (message.sender === 'landlord') return;
        if (this.isMessageSeen(message)) return;
        if (this.trackedVisibilityMessages.has(message.id)) return;

        this.trackedVisibilityMessages.add(message.id);
        const element = this.$refs.messages[i];
        this.messagesObserver.observe(element);
      });
    },

    storeLastSeen(message) {
      // API doesn't support cancelling requests
      // and we need to make sure we don't send multiple requests
      // because order matters
      if (this.storeLastSeenActiveRequest) {
        this.storeLastSeenBufferValue = message;
        return;
      }

      this.storeLastSeenActiveRequest = api
        .postLandlordInterestManagementCandidateMessageMarkAsRead(this.landlordId, this.iMId, this.prospectId, {
          body: { message_id: message.id },
        })
        .finally(() => {
          this.storeLastSeenActiveRequest = undefined;
          if (this.storeLastSeenBufferValue) {
            this.storeLastSeen(this.storeLastSeenBufferValue);
            this.storeLastSeenBufferValue = undefined;
          }
        });
    },

    setLastSeen(message) {
      this.lastSeenDate = message.date;

      this.storeLastSeenDebounced(message);
    },
  },

  mounted() {
    const element = this.$refs.messagesContainer;
    element.scrollTo({ top: element.scrollHeight });
    element.addEventListener('scroll', this.handleScroll);

    this.updateLocalLastSeenDate();

    this.messagesObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            const index = entry.target.getAttribute('data-index');
            const message = this.prospect.messages[index];
            if (this.isMessageSeen(message)) return;

            this.setLastSeen(message);
          }
        });
      },
      {
        root: this.$refs.messagesContainer,
        threshold: 0.8,
      }
    );

    this.trackMessagesVisibility();
  },

  beforeUnmount() {
    const element = this.$refs.messagesContainer;
    element.removeEventListener('scroll', this.handleScroll);
    this.trackedVisibilityMessages.clear();

    this.messagesObserver.disconnect();
  },

  watch: {
    'prospect.messages'() {
      this.updateLocalLastSeenDate();

      this.$nextTick(function () {
        const element = this.$refs.messagesContainer;
        element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });

        this.trackMessagesVisibility();
      });
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.newMessage.length) {
      const answer = window.confirm(`Bytter du fane forsvinner teksten`);
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>

<style scoped lang="scss">
.scrollUp {
  position: sticky;
  top: 0;
  width: 100%;

  color: $gray-800;
  text-align: center;
  padding: 1rem;
  z-index: 1;
  cursor: pointer;
  transition: $transition-base;
}

.scrollUp.hidden {
  background: transparent;
  color: transparent;
}

.conversation-container {
  height: 500px;
  position: relative;
  overflow-y: scroll;
  background: $light;
  padding-bottom: 0rem;
}

.message {
  padding: 0.7rem;
  border-radius: 1rem;
  width: auto;
  max-width: 100%;
  font-family: inherit;
  font-size: inherit;
  overflow-x: auto;
  white-space: pre-line;
}

.date-stamp {
  border-bottom: 1px solid $gray-300;
  margin-bottom: 2rem;
  text-align: center;

  .date-stamp-inner {
    background: $light;
    color: $gray-700;
    border-radius: 2rem;
    padding: 0.2rem 1.5rem;
    font-size: 0.7rem;
    position: relative;
    top: 8px;
  }
}

.message-details {
  margin-top: 0.7rem;
  font-size: 0.7rem;
  color: $gray-600;
}

.from {
  margin-bottom: 0.5rem;
  font-size: 0.7rem;
  font-weight: bold;
}

.timestamp {
  font-size: 0.7rem;
  color: $gray-600;
}

.chat {
  margin-bottom: 2rem;
}

.chat-origin {
  text-align: right;

  .message {
    text-align: left;
    background: $info;
    color: white;
    border-bottom-right-radius: 0;
  }

  .from {
    color: $info;
  }
}

.chat-destination {
  text-align: left;

  .message {
    background: white;
    color: $secondary;
    border-bottom-left-radius: 0;
    border: 1px solid $gray-200;
  }

  .from {
    color: $secondary;
  }
}

.chat-system {
  text-align: right;

  .message {
    text-align: left;
    background: $info;
    color: white;
    border-bottom-right-radius: 0;
  }

  .from {
    color: $info;
  }
}

.sendMessageSection {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 0rem;
  padding-top: 1.5rem;
  border-top: 1px solid $gray-300;
  grid-template-rows: 100px;
}

.sendMessageSection textarea {
  padding: 0.7rem 1rem;
  border: 1px solid #d5d7da;
  outline: 0;
  border-radius: 10px;
}

@media (min-width: 450px) {
  .sendMessageSection {
    display: grid;
    grid-template-columns: 1fr min-content;
    gap: 0rem 1rem;
    grid-template-rows: 50px;
  }
}
</style>
